import { Controller } from "@hotwired/stimulus"

// Handles inline editing of agreement fields
// Fields use contenteditable and save on blur
export default class extends Controller {
  static targets = ["field"]
  static values = {
    packageId: Number,
    updateUrl: String
  }

  connect() {
    this.originalValues = new Map()
    this.handleExternalUpdate = this.handleExternalUpdate.bind(this)
    window.addEventListener("agreement-field-updated", this.handleExternalUpdate)
  }

  disconnect() {
    window.removeEventListener("agreement-field-updated", this.handleExternalUpdate)
  }

  // Called when another UI surface (e.g. Details slideover) updates an
  // agreement field. Update any matching inline spans so the rendered AST
  // reflects the change without a page reload.
  handleExternalUpdate(event) {
    const { fieldName, formattedValue, rawValue, source } = event.detail || {}
    if (source === "inline") return // ignore our own events

    this.fieldTargets
      .filter((f) => f.dataset.field === fieldName)
      .forEach((f) => {
        if (f.dataset.type === "textarea") {
          f.innerHTML = this.renderTextareaParagraphs(rawValue)
        } else {
          f.textContent = formattedValue || ""
        }
        f.dataset.rawValue = rawValue || ""
        if (rawValue || formattedValue) {
          f.removeAttribute("data-empty")
          f.classList.remove("empty")
        }
      })
  }

  renderTextareaParagraphs(value) {
    if (!value) return "<p></p>"
    const escape = (s) =>
      s.replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
    return value
      .split(/\n{2,}/)
      .map((para) => `<p>${escape(para).replace(/\n/g, "<br>")}</p>`)
      .join("")
  }

  notifyOtherSurfaces(fieldName, rawValue, formattedValue) {
    window.dispatchEvent(new CustomEvent("agreement-field-updated", {
      detail: { fieldName, rawValue, formattedValue, source: "inline" }
    }))
  }

  startEdit(event) {
    const field = event.currentTarget
    // Store original value for potential revert
    this.originalValues.set(field.dataset.field, field.textContent)

    // Clear placeholder text for empty fields
    if (field.dataset.empty === "true") {
      field.textContent = ""
      field.removeAttribute("data-empty")
      field.classList.remove("empty")
    }
  }

  handleKeydown(event) {
    // Save on Enter (without shift), cancel on Escape
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      event.currentTarget.blur()
    } else if (event.key === "Escape") {
      event.preventDefault()
      const field = event.currentTarget
      const originalValue = this.originalValues.get(field.dataset.field)
      if (originalValue) {
        field.textContent = originalValue
        // Restore empty state if original was a placeholder
        if (originalValue.startsWith("[") && originalValue.endsWith("]")) {
          field.dataset.empty = "true"
          field.classList.add("empty")
        }
      }
      field.blur()
    }
  }

  async save(event) {
    const field = event.currentTarget
    const fieldName = field.dataset.field
    const fieldType = field.dataset.type
    const newValue = field.textContent.trim()
    const originalValue = this.originalValues.get(fieldName)

    // Don't save if value hasn't changed
    if (newValue === originalValue) {
      return
    }

    // If empty and original was a placeholder, restore it instead of saving
    if (newValue === "" && originalValue && originalValue.startsWith("[") && originalValue.endsWith("]")) {
      field.textContent = originalValue
      field.dataset.empty = "true"
      field.classList.add("empty")
      return
    }

    // Parse value based on type for storage
    const valueToSave = this.parseValue(newValue, fieldType)

    field.classList.add("saving")

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          field_name: fieldName,
          value: valueToSave
        })
      })

      const data = await response.json()

      if (data.needs_confirmation) {
        // Show confirmation modal
        const confirmed = await this.showConfirmationModal(data.message)
        if (confirmed) {
          // Retry with confirmation
          return this.saveWithConfirmation(field, fieldName, valueToSave)
        } else {
          // Revert to original value
          field.textContent = originalValue || `[${fieldName}]`
        }
      } else if (data.success) {
        // Update display with formatted value
        field.textContent = data.formatted_value
        field.dataset.rawValue = valueToSave
        field.classList.add("has-override")
        this.showSavedFeedback(field)
        this.notifyOtherSurfaces(fieldName, valueToSave, data.formatted_value)

        // If status changed, reload the page to update UI
        if (data.status_changed) {
          window.location.reload()
        }
      } else {
        // Revert on error
        field.textContent = originalValue || `[${fieldName}]`
        console.error("Save failed:", data.errors)
      }
    } catch (error) {
      console.error("Save error:", error)
      field.textContent = originalValue || `[${fieldName}]`
    } finally {
      field.classList.remove("saving")
    }
  }

  async saveWithConfirmation(field, fieldName, value) {
    field.classList.add("saving")

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          field_name: fieldName,
          value: value,
          confirmed: "true"
        })
      })

      const data = await response.json()

      if (data.success) {
        field.textContent = data.formatted_value
        field.dataset.rawValue = value
        field.classList.add("has-override")
        this.showSavedFeedback(field)
        this.notifyOtherSurfaces(fieldName, value, data.formatted_value)

        // Reload if status changed to draft
        if (data.status_changed) {
          window.location.reload()
        }
      }
    } catch (error) {
      console.error("Save with confirmation error:", error)
    } finally {
      field.classList.remove("saving")
    }
  }

  parseValue(displayValue, type) {
    switch (type) {
      case "currency":
        // Remove currency symbol and commas
        return displayValue.replace(/[^0-9.]/g, "")
      case "date":
        // Try to parse the date - this is tricky, keep as-is for now
        // Server will handle parsing
        return displayValue
      case "ordinal":
        // Extract just the number
        return displayValue.replace(/[^0-9]/g, "")
      default:
        return displayValue
    }
  }

  showConfirmationModal(message) {
    return new Promise((resolve) => {
      // Use native confirm for now - can be upgraded to custom modal later
      const result = confirm(message + "\n\nDo you want to continue?")
      resolve(result)
    })
  }

  showSavedFeedback(field) {
    field.classList.add("saved")
    setTimeout(() => {
      field.classList.remove("saved")
    }, 500)
  }
}
